import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import usePlayer from '../../contexts/usePlayer';
import useToast from '../../contexts/useToast';
import { useItem } from '../../contexts/ItemContext';
import type { ParsedSentence } from '../../../../types';
import {
  timeToSentenceIndices,
  sentenceIndicesToTime,
} from './transcriptUtils';
import SelectionBubble from './SelectionBubble';
import SentenceRenderer, {
  HeadingRenderer,
  ImageRenderer,
  ImageBlockRenderer,
} from './SentenceRenderer';

const TIMING_OFFSET = 0.2;
const AUTO_SCROLL_RESUME_DELAY = 3000;

interface TranscriptAreaProps {
  contentId: string;
  sentences: ParsedSentence[];
  timeMap: number[];
  coverImageUrl?: string | null;
  coverAlt?: string;
  textSize?: number; // -2 to +2, default 0
  initialTime?: number; // Seconds to initially scroll to on mount
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}

export interface TranscriptAreaRef {
  containerRef: React.RefObject<HTMLDivElement | null>;
  scrollToSentence: (sentenceIdx: number) => void;
}

const TranscriptArea = forwardRef<TranscriptAreaRef, TranscriptAreaProps>(
  function TranscriptArea(
    {
      contentId,
      sentences,
      timeMap,
      coverImageUrl,
      coverAlt,
      textSize = 0,
      initialTime,
      scrollContainerRef,
    }: TranscriptAreaProps,
    ref: React.ForwardedRef<TranscriptAreaRef>
  ) {
    // Calculate font sizes based on textSize (-2 to +2)
    const baseFontSize = 1.1 + textSize * 0.15; // 0.8rem to 1.4rem
    const headingFontSize = 1.4 + textSize * 0.15; // 1.1rem to 1.7rem
    const {
      nowPlayingId,
      isPlaying,
      currentTime,
      seekTo,
      play: playerPlay,
    } = usePlayer();

    const isNowPlaying = contentId === nowPlayingId;
    const isCurrentlyPlaying = isNowPlaying && isPlaying;
    const { showToast } = useToast();
    const {
      content,
      selection,
      setSelection,
      clearSelection,
      hasSelection,
      loadingMore,
      isPlaylist,
      currentItem,
    } = useItem();

    const internalContainerRef = useRef<HTMLDivElement>(null);
    // Callback ref to assign the scroll container to both internal and external refs
    const containerCallbackRef = useCallback(
      (node: HTMLDivElement | null) => {
        (
          internalContainerRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = node;
        if (scrollContainerRef) {
          (
            scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>
          ).current = node;
        }
      },
      [scrollContainerRef]
    );
    // Use internalContainerRef for all reads
    const containerRef = internalContainerRef;
    const wordRefs = useRef<Map<string, HTMLSpanElement>>(new Map());
    const sentenceRefs = useRef<Map<number, HTMLSpanElement>>(new Map());
    const lastManualScrollTime = useRef(0);
    const isAutoScrolling = useRef(false);

    // Expose containerRef and scrollToSentence to parent
    useImperativeHandle(ref, () => ({
      containerRef,
      scrollToSentence: (sentenceIdx: number) => {
        const el = sentenceRefs.current.get(sentenceIdx);
        const container = containerRef.current;
        if (el && container) {
          const containerRect = container.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          // Calculate scroll position to center the element
          const elTop = elRect.top - containerRect.top + container.scrollTop;
          const centerOffset = (containerRect.height - elRect.height) / 2;
          const targetScroll = elTop - centerOffset;
          container.scrollTo({ top: targetScroll, behavior: 'smooth' });
        }
      },
    }));

    // Initial scroll to listening position - runs before paint to avoid flash
    const hasInitialScrolled = useRef(false);
    const currentTimeRef = useRef(currentTime);
    currentTimeRef.current = currentTime;

    // Reset initial scroll flag on content change
    useEffect(() => {
      hasInitialScrolled.current = false;
    }, [contentId]);

    // Instant scroll to current position on first render
    useLayoutEffect(() => {
      if (hasInitialScrolled.current) return;
      if (!containerRef.current || !sentences.length || !timeMap.length) return;

      const targetTime = isNowPlaying
        ? currentTimeRef.current
        : (initialTime ?? 0);
      if (targetTime <= 0) {
        hasInitialScrolled.current = true;
        return;
      }

      // Binary search for sentence at target time
      let lo = 0,
        hi = timeMap.length - 1;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi + 1) / 2);
        const midTime = timeMap[mid];
        if (midTime !== undefined && midTime <= targetTime) lo = mid;
        else hi = mid - 1;
      }

      const el = sentenceRefs.current.get(lo);
      if (el) {
        el.scrollIntoView({
          behavior: 'instant' as ScrollBehavior,
          block: 'center',
        });
      }
      hasInitialScrolled.current = true;
    }, [sentences, timeMap, isNowPlaying, initialTime, contentId]);

    // Local anchor state for first-click (before completing selection)
    const [anchorIdx, setAnchorIdx] = useState<number | null>(null);
    const [bubbleState, setBubbleState] = useState<{
      top: number | null;
      isOutOfView: 'above' | 'below' | null;
    }>({ top: null, isOutOfView: null });

    // Sliding underline - use ref + direct DOM manipulation for performance
    const underlineRef = useRef<HTMLDivElement>(null);
    const lastUnderlineTop = useRef<number>(0);
    const underlineVisible = useRef(false);

    // Convert selection to sentence indices
    const selectionIndices = useMemo(() => {
      return timeToSentenceIndices(selection, timeMap);
    }, [selection, timeMap]);

    const hasAnchorOnly = anchorIdx !== null && !hasSelection;
    const hasFullSelection = hasSelection;

    // Find current sentence via binary search
    const currentSentenceIdx = useMemo(() => {
      if (!timeMap.length) return 0;
      let lo = 0,
        hi = timeMap.length - 1;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi + 1) / 2);
        const midTime = timeMap[mid];
        if (midTime !== undefined && midTime <= currentTime) lo = mid;
        else hi = mid - 1;
      }
      return lo;
    }, [timeMap, currentTime]);

    // Find current word within the current sentence
    const currentWordInfo = useMemo(() => {
      const sentence = sentences[currentSentenceIdx];
      if (!sentence?.words?.length) return null;

      const adjusted = currentTime + TIMING_OFFSET;
      let wordIdx = 0;
      for (let i = 0; i < sentence.words.length; i++) {
        const word = sentence.words[i];
        if (word && adjusted >= word.start) wordIdx = i;
      }

      return { sentenceIdx: currentSentenceIdx, wordIdx };
    }, [sentences, currentSentenceIdx, currentTime]);

    // Track manual scrolling
    const handleScroll = useCallback(() => {
      if (!isAutoScrolling.current) {
        lastManualScrollTime.current = Date.now();
      }
    }, []);

    // Track previous textSize to detect changes
    const prevTextSizeRef = useRef(textSize);

    // Update sliding underline position using requestAnimationFrame for smooth performance
    useEffect(() => {
      const underline = underlineRef.current;
      if (!underline) return;

      if (!isNowPlaying || !currentWordInfo) {
        underline.style.opacity = '0';
        underlineVisible.current = false;
        return;
      }

      const wordKey = `${currentWordInfo.sentenceIdx}-${currentWordInfo.wordIdx}`;
      const wordEl = wordRefs.current.get(wordKey);
      const container = containerRef.current;

      // Check if textSize changed - if so, skip transition
      const textSizeChanged = prevTextSizeRef.current !== textSize;
      prevTextSizeRef.current = textSize;

      if (wordEl && container) {
        // Use setTimeout to allow DOM to reflow after text size change
        const update = () => {
          const containerRect = container.getBoundingClientRect();
          const wordRect = wordEl.getBoundingClientRect();

          const newTop =
            wordRect.bottom - containerRect.top + container.scrollTop - 1;
          const newLeft =
            wordRect.left - containerRect.left + container.scrollLeft;

          // Detect line change or text size change - disable transition
          const isNewLine = Math.abs(newTop - lastUnderlineTop.current) > 10;
          lastUnderlineTop.current = newTop;

          // Disable transition for line jumps or text size changes
          underline.style.transition =
            isNewLine || textSizeChanged
              ? 'none'
              : 'left 0.08s linear, width 0.08s linear';
          underline.style.left = `${newLeft - 3}px`;
          underline.style.top = `${newTop}px`;
          underline.style.width = `${wordRect.width + 6}px`;
          underline.style.opacity = '1';
          underlineVisible.current = true;

          // If text size changed, also scroll to keep the word in view
          if (textSizeChanged) {
            const isInView =
              wordRect.top >= containerRect.top + 60 &&
              wordRect.bottom <= containerRect.bottom - 60;
            if (!isInView) {
              wordEl.scrollIntoView({ behavior: 'auto', block: 'center' });
            }
          }
        };

        if (textSizeChanged) {
          // Small delay to let DOM reflow after text size change
          setTimeout(() => requestAnimationFrame(update), 10);
        } else {
          requestAnimationFrame(update);
        }
      }
    }, [currentWordInfo, isNowPlaying, textSize]);

    // Auto-scroll to current word during playback
    useEffect(() => {
      if (!isCurrentlyPlaying || !currentWordInfo) return;

      const timeSinceManualScroll = Date.now() - lastManualScrollTime.current;
      if (timeSinceManualScroll < AUTO_SCROLL_RESUME_DELAY) return;

      const wordKey = `${currentWordInfo.sentenceIdx}-${currentWordInfo.wordIdx}`;
      const wordEl = wordRefs.current.get(wordKey);

      if (wordEl && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const wordRect = wordEl.getBoundingClientRect();
        const isInView =
          wordRect.top >= containerRect.top + 100 &&
          wordRect.bottom <= containerRect.bottom - 100;

        if (!isInView) {
          isAutoScrolling.current = true;
          wordEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => {
            isAutoScrolling.current = false;
          }, 500);
        }
      }
    }, [currentWordInfo, isCurrentlyPlaying]);

    // Handle sentence click for selection
    const handleSentenceClick = useCallback(
      (idx: number, e: React.MouseEvent) => {
        // Shift+click extends selection
        if (e.shiftKey && (selectionIndices || anchorIdx !== null)) {
          const start = selectionIndices?.startIdx ?? anchorIdx!;
          const times = sentenceIndicesToTime(
            Math.min(start, idx),
            Math.max(start, idx),
            timeMap
          );
          if (times) {
            setSelection(times.startSeconds, times.endSeconds);
          }
          setAnchorIdx(null);
          return;
        }

        // If we have a time selection, clicking anywhere clears it and sets new anchor
        if (hasSelection) {
          clearSelection();
          setAnchorIdx(idx);
          return;
        }

        // If clicking the anchor, deselect
        if (anchorIdx === idx) {
          setAnchorIdx(null);
          return;
        }

        // If anchor exists, complete the range
        if (anchorIdx !== null) {
          const times = sentenceIndicesToTime(
            Math.min(anchorIdx, idx),
            Math.max(anchorIdx, idx),
            timeMap
          );
          if (times) {
            setSelection(times.startSeconds, times.endSeconds);
          }
          setAnchorIdx(null);
          return;
        }

        // Otherwise start new selection with this as anchor
        setAnchorIdx(idx);
      },
      [
        selectionIndices,
        anchorIdx,
        hasSelection,
        clearSelection,
        setSelection,
        timeMap,
      ]
    );

    // Clear selection and anchor
    const handleClearSelection = useCallback(() => {
      clearSelection();
      setAnchorIdx(null);
    }, [clearSelection]);

    // Play from selection start
    const handlePlayFromSelection = useCallback(() => {
      const startIdx = selectionIndices?.startIdx ?? anchorIdx;
      if (startIdx === null) return;

      const startTime = timeMap[startIdx] ?? 0;

      if (isNowPlaying) {
        // Seek to position, then resume playback if paused
        seekTo(startTime);
        if (!isPlaying) {
          // Call play() with no args to resume
          playerPlay();
        }
      } else {
        // Start playing this content from the selected position
        // For playlists, play the current chapter item, not the playlist
        const itemToPlay = isPlaylist && currentItem ? currentItem : content;
        playerPlay(itemToPlay, true, true);
        // Small delay to let the player initialize, then seek
        setTimeout(() => seekTo(startTime), 100);
      }

      handleClearSelection();
    }, [
      selectionIndices,
      anchorIdx,
      timeMap,
      isNowPlaying,
      isPlaying,
      seekTo,
      playerPlay,
      content,
      isPlaylist,
      currentItem,
      handleClearSelection,
    ]);

    // Copy selected text
    const handleCopySelection = useCallback(() => {
      const startIdx = selectionIndices?.startIdx ?? anchorIdx;
      const endIdx = selectionIndices?.endIdx ?? anchorIdx;

      if (startIdx === null || endIdx === null) return;

      const minIdx = Math.min(startIdx, endIdx);
      const maxIdx = Math.max(startIdx, endIdx);

      const selectedText = sentences
        .slice(minIdx, maxIdx + 1)
        .map((s) => s.text)
        .join(' ');

      navigator.clipboard.writeText(selectedText).then(
        () => {
          showToast('Copied to clipboard', 'success');
          handleClearSelection();
        },
        () => {
          showToast('Failed to copy', 'error');
        }
      );
    }, [
      selectionIndices,
      anchorIdx,
      sentences,
      showToast,
      handleClearSelection,
    ]);

    // Scroll to selection
    const scrollToSelection = useCallback(() => {
      const startIdx = selectionIndices?.startIdx ?? anchorIdx;
      if (startIdx === null) return;
      const el = sentenceRefs.current.get(startIdx);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, [selectionIndices, anchorIdx]);

    // Calculate bubble position based on selection
    useEffect(() => {
      const startIdx = selectionIndices?.startIdx ?? anchorIdx;
      const container = containerRef.current;

      if (startIdx === null || !container) {
        setBubbleState({ top: null, isOutOfView: null });
        return;
      }

      const updatePosition = () => {
        const sentenceEl = sentenceRefs.current.get(startIdx);
        if (!sentenceEl || !container) return;

        try {
          const containerRect = container.getBoundingClientRect();
          const sentenceRect = sentenceEl.getBoundingClientRect();

          const bubbleHeight = 36;
          const relativeTop =
            sentenceRect.top -
            containerRect.top +
            container.scrollTop -
            bubbleHeight -
            8;

          const isAbove = sentenceRect.bottom < containerRect.top;
          const isBelow = sentenceRect.top > containerRect.bottom;
          const isInView = !isAbove && !isBelow;

          // Minimum position for the bubble - below the top fade gradient
          const minTop = container.scrollTop + 60;

          if (isInView) {
            // Clamp bubble position to never go above the minimum
            const clampedTop = Math.max(relativeTop, minTop);
            setBubbleState({ top: clampedTop, isOutOfView: null });
          } else if (isBelow) {
            setBubbleState({ top: null, isOutOfView: 'below' });
          } else if (isAbove) {
            setBubbleState({ top: null, isOutOfView: 'above' });
          }
        } catch {
          // Ignore errors from getBoundingClientRect on detached elements
        }
      };

      updatePosition();
      container.addEventListener('scroll', updatePosition);
      return () => {
        try {
          container.removeEventListener('scroll', updatePosition);
        } catch {
          // Ignore cleanup errors
        }
      };
    }, [selectionIndices, anchorIdx]);

    // Calculate selection info for bubble
    const selectionInfo = useMemo(() => {
      if (
        hasFullSelection &&
        selectionIndices &&
        selection.startSeconds !== null &&
        selection.endSeconds !== null
      ) {
        const count =
          Math.abs(selectionIndices.endIdx - selectionIndices.startIdx) + 1;
        const duration = Math.floor(
          selection.endSeconds - selection.startSeconds
        );
        return { count, duration };
      }
      if (hasAnchorOnly && anchorIdx !== null) {
        const nextTime = timeMap[anchorIdx + 1];
        const thisTime = timeMap[anchorIdx];
        const duration =
          nextTime !== undefined && thisTime !== undefined
            ? Math.floor(nextTime - thisTime)
            : 3;
        return { count: 1, duration };
      }
      return null;
    }, [
      hasFullSelection,
      hasAnchorOnly,
      selectionIndices,
      selection,
      anchorIdx,
      timeMap,
    ]);

    // Check if a sentence is selected
    const isSentenceSelected = useCallback(
      (idx: number) => {
        if (anchorIdx === idx) return true;
        if (!selectionIndices) return false;
        return (
          idx >= selectionIndices.startIdx && idx <= selectionIndices.endIdx
        );
      },
      [anchorIdx, selectionIndices]
    );

    const isAnchorSentence = useCallback(
      (idx: number) => anchorIdx === idx,
      [anchorIdx]
    );

    // Group sentences into paragraphs and image blocks
    const renderContent = () => {
      const elements: React.ReactNode[] = [];
      let currentParagraph: { sentence: ParsedSentence; idx: number }[] = [];
      let currentImageBlock: { sentence: ParsedSentence; idx: number }[] = [];
      let currentImageUrl: string | null = null;

      const flushParagraph = () => {
        if (currentParagraph.length === 0) return;
        const firstSentence = currentParagraph[0];
        if (!firstSentence) return;

        elements.push(
          <p
            key={`p-${firstSentence.idx}`}
            style={{
              margin: '0 0 1.5em 0',
              lineHeight: 1.8,
              fontSize: `${baseFontSize}rem`,
              textAlign: 'left',
            }}
          >
            {currentParagraph.map(({ sentence, idx }) => (
              <SentenceRenderer
                key={idx}
                sentence={sentence}
                sentenceIdx={idx}
                currentSentenceIdx={currentSentenceIdx}
                currentWordInfo={currentWordInfo}
                isNowPlaying={isNowPlaying}
                isSentenceSelected={isSentenceSelected(idx)}
                isSentenceAnchor={isAnchorSentence(idx)}
                onSentenceClick={handleSentenceClick}
                sentenceRefs={sentenceRefs}
                wordRefs={wordRefs}
              />
            ))}
          </p>
        );
        currentParagraph = [];
      };

      const flushImageBlock = () => {
        if (currentImageBlock.length === 0) return;
        const firstSentence = currentImageBlock[0];
        if (!firstSentence) return;

        // If it's a single image-only sentence (no text), use simple ImageRenderer
        if (currentImageBlock.length === 1 && !firstSentence.sentence.text) {
          elements.push(
            <ImageRenderer
              key={`img-${firstSentence.idx}`}
              sentence={firstSentence.sentence}
              sentenceIdx={firstSentence.idx}
              currentSentenceIdx={currentSentenceIdx}
              isNowPlaying={isNowPlaying}
              hasActiveSelection={hasAnchorOnly || hasFullSelection}
              sentenceRefs={sentenceRefs}
            />
          );
        } else {
          // Multiple sentences or sentences with text - use ImageBlockRenderer
          elements.push(
            <ImageBlockRenderer
              key={`img-block-${firstSentence.idx}`}
              sentences={currentImageBlock.map((item) => item.sentence)}
              startIdx={firstSentence.idx}
              currentSentenceIdx={currentSentenceIdx}
              currentWordInfo={currentWordInfo}
              isNowPlaying={isNowPlaying}
              baseFontSize={baseFontSize}
              hasActiveSelection={hasAnchorOnly || hasFullSelection}
              isSentenceSelected={isSentenceSelected}
              isAnchorSentence={isAnchorSentence}
              onSentenceClick={handleSentenceClick}
              sentenceRefs={sentenceRefs}
              wordRefs={wordRefs}
            />
          );
        }
        currentImageBlock = [];
        currentImageUrl = null;
      };

      sentences.forEach((sentence, idx) => {
        // Handle headings
        if (sentence.heading) {
          flushParagraph();
          flushImageBlock();

          elements.push(
            <HeadingRenderer
              key={`h-${idx}`}
              sentence={sentence}
              sentenceIdx={idx}
              currentSentenceIdx={currentSentenceIdx}
              isNowPlaying={isNowPlaying}
              isSentenceSelected={isSentenceSelected(idx)}
              isSentenceAnchor={isAnchorSentence(idx)}
              headingFontSize={headingFontSize}
              onSentenceClick={handleSentenceClick}
              sentenceRefs={sentenceRefs}
            />
          );
        }
        // Handle sentences with imageUrl (transcribed image content)
        else if (sentence.imageUrl) {
          flushParagraph();

          // Check if this is part of the current image block
          if (sentence.imageUrl === currentImageUrl) {
            currentImageBlock.push({ sentence, idx });
          } else {
            // Different image - flush the old block and start new one
            flushImageBlock();
            currentImageUrl = sentence.imageUrl;
            currentImageBlock.push({ sentence, idx });
          }
        }
        // Regular text sentences
        else {
          flushImageBlock();
          // Start new paragraph if sentence is marked as paragraph start
          if (sentence.startParagraph && currentParagraph.length > 0) {
            flushParagraph();
          }
          currentParagraph.push({ sentence, idx });
        }
      });

      flushParagraph();
      flushImageBlock();
      return elements;
    };

    if (sentences.length === 0) {
      return (
        <div className="transcript-area">
          <div
            className="transcript-scroll"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                border: '2px solid var(--border)',
                borderTopColor: 'var(--accent)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
              Loading transcript...
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="transcript-area">
        {/* Scrollable transcript content */}
        <div
          ref={containerCallbackRef}
          onScroll={handleScroll}
          className="transcript-scroll"
        >
          {coverImageUrl && (
            <img
              src={coverImageUrl}
              alt={coverAlt || ''}
              style={{
                display: 'block',
                width: '100%',
                maxHeight: '42vh',
                objectFit: 'contain',
                borderRadius: 8,
                margin: '0 0 28px',
                background: 'var(--card-bg)',
                boxShadow: 'var(--shadow-md)',
              }}
            />
          )}

          {renderContent()}

          {/* Loading indicator for additional sentences */}
          {loadingMore && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '20px',
                color: 'var(--text-secondary)',
              }}
            >
              <div
                className="spinner"
                style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid var(--border)',
                  borderTopColor: 'var(--accent)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            </div>
          )}

          {/* Sliding underline for current word */}
          <div
            ref={underlineRef}
            style={{
              position: 'absolute',
              height: '6px',
              backgroundColor: 'var(--teal)',
              borderRadius: '3px',
              pointerEvents: 'none',
              opacity: 0,
            }}
          />

          {/* Floating selection bubble - when in view */}
          {selectionInfo && bubbleState.top !== null && (
            <div
              style={{
                position: 'absolute',
                top: bubbleState.top,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
              }}
            >
              <SelectionBubble
                onPlayFromSelection={handlePlayFromSelection}
                onCopySelection={handleCopySelection}
                onClearSelection={handleClearSelection}
              />
              {hasAnchorOnly && (
                <div
                  style={{
                    padding: '3px 10px',
                    background: 'rgba(42, 42, 42, 0.75)',
                    borderRadius: '10px',
                    color: 'rgba(255,255,255,0.75)',
                    fontSize: '0.65rem',
                    fontStyle: 'italic',
                  }}
                >
                  click another to select range
                </div>
              )}
            </div>
          )}
        </div>

        {/* Fixed selection indicator - when out of view */}
        {selectionInfo && bubbleState.isOutOfView && (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2px',
              ...(bubbleState.isOutOfView === 'above'
                ? { top: 48 }
                : { bottom: 48 }),
            }}
          >
            <SelectionBubble
              isFixed
              isOutOfViewBelow={bubbleState.isOutOfView === 'below'}
              onScrollToSelection={scrollToSelection}
              onPlayFromSelection={handlePlayFromSelection}
              onCopySelection={handleCopySelection}
              onClearSelection={handleClearSelection}
            />
            {hasAnchorOnly && (
              <div
                style={{
                  padding: '3px 10px',
                  background: 'rgba(42, 42, 42, 0.75)',
                  borderRadius: '10px',
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: '0.65rem',
                  fontStyle: 'italic',
                }}
              >
                click another to select range
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

export default TranscriptArea;
